import moment from 'moment-business-days';
import {
  FIXED,
  MOBILE,
  NETWORK_IT_SECURIT_ASSESMENT_FIXED,
  preImp6Yes,
  GENERAL_INFORMATION_FIXED,
  GENERAL_INFORMATION_MOBILE,
} from 'AppSrc/core/components/forms/value-dependency-constants';
import { getPreImp6Value } from 'AppSrc/core/components/forms/form-types/create-crq/common-field-selectors';

import { WORK_INFORMATION_ITEMS } from 'AppSrc/core/components/forms/form-types/create-crq/form-definition/section-constants';
import { getFormDataFieldValue } from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import {
  WORK_INFORMATION_ITEMS_FIELD_ID,
  PEER_REJECTED_TYPE,
  WORK_INFORMATION_TYPE,
  CANCELLATION_INFORMATION,
  SUMMARY,
  fixedHumanCreatableWIT,
  mobileHumanCreatableWIT,
  fixedAttachmentMandatoryWIT,
  mobileAttachmentMandatoryWIT,
  noDuplicatesWorkInfoTypesMobile,
  generalInfoAutoGeneratedTexts,
} from './work-information-item-constants';


const isWitHumanCreatable = (params) => {
  const {
    selectedNetwork,
    workInfoItemType,
    formData,
  } = params;

  const oloNetwork = (selectedNetwork === 'fixedOlo' || selectedNetwork === 'fixedInternational') ? 'fixed' : selectedNetwork;


  if (oloNetwork === FIXED) {

    // this work info item is selectable if preImp6 is answered with yes
    const preImp6Value = getPreImp6Value(formData);
    if (workInfoItemType === NETWORK_IT_SECURIT_ASSESMENT_FIXED) {
      if (preImp6Value === preImp6Yes) {
        return true;
      }
    }

    return fixedHumanCreatableWIT.includes(workInfoItemType);
  }

  if (oloNetwork === MOBILE) {
    return mobileHumanCreatableWIT.includes(workInfoItemType);
  }

  console.error(`Unable to determine if the user should be able to view work information item for selectedNetwork: ${oloNetwork}, workInfoItemType: ${workInfoItemType}`);
  // Worse case scenerio is the user has access to WIT they should not be able to edit
  return false;

};

const isAttachmentMadatory = (network, selectedWorkInformationType) => {

  if (network === FIXED) {
    return fixedAttachmentMandatoryWIT.includes(selectedWorkInformationType);
  }

  if (network === MOBILE) {
    return mobileAttachmentMandatoryWIT.includes(selectedWorkInformationType);
  }

  console.error(`Unable to determine if attachment is mandatory for network: ${network}, selectedWorkInformationType: ${selectedWorkInformationType}`);
  // fallback to false? Most don't require attachments...
  return false;

};

const getNoDuplicatesWorkInfoType = (network, workInformationType) => {

  if (network === MOBILE) {
    return noDuplicatesWorkInfoTypesMobile.includes(workInformationType);
  }

  if (network === FIXED) {
    // currently you can have duplicates of all WITs on fixed
    return false;
  }

  if (!network) {
    console.error(`Unable to determine if duplications of work information type are allowed for network: ${network}, workInformationType: ${workInformationType}`);
  }

  // fallback to true as I imagine something like this to breaking on the endpoint...
  return true;

};

// collect work info item metadata from the form data, with their id type and subject
// this will be useful for displaying the approvals history
const getWiiMetadata = (formData) => {
  const wiis = [];
  const wiiFormData = getFormDataFieldValue({
    formData,
    sectionId: WORK_INFORMATION_ITEMS,
    fieldId: WORK_INFORMATION_ITEMS_FIELD_ID,
  });
  const wiiSectionIds = wiiFormData && wiiFormData.formSectionsByDisplayOrder ? wiiFormData.formSectionsByDisplayOrder : [];
  wiiSectionIds.forEach((sectionId) => {
    const sectionData = wiiFormData ? wiiFormData.dataByFormSectionId[sectionId] : null;
    const notes = sectionData ? sectionData.notes : null;
    const type = getFormDataFieldValue({
      formData: wiiFormData,
      sectionId,
      fieldId: WORK_INFORMATION_TYPE,
    });
    const summary = getFormDataFieldValue({
      formData: wiiFormData,
      sectionId,
      fieldId: SUMMARY,
    });
    wiis.push({
      id: sectionId,
      type,
      summary,
      notes,
    });
  });
  return wiis;
};

const hasPeerRejectionWii = (formData, network) => {
  if (!formData) { return false; }
  const wiis = getWiiMetadata(formData);
  const rejectionType = PEER_REJECTED_TYPE[network];
  return wiis.some((wii) => wii.type === rejectionType);
};


/*
DVB: Deprecating the summary code, as we need to be able to support
data which has new lines in it. For speed of development we will change
the functionality to use JSON, as it's not really necessary for the metadata to
be human readable

const getStandardisedSummary = (fields) => {
  const keys = Object.keys(fields);
  let summary = '';
  keys.forEach((key) => {
    // capitalises first letter, ie name -> Name
    const capKey = key.charAt(0).toUpperCase() + key.slice(1);
    summary = `${summary}${capKey}: ${fields[key]}\n`;
  });

  return summary;
};



const keyAliasMap = {
  Date: 'date',
  Group: 'group',
  Peer: 'peer',
  Status: 'status',
  Type: 'type',
  CancelledBy: 'cancelledBy',
};

// We convert an object into a human readable work info item. This is great, but we then need to convert it back into an object
// again. As such we have to find the proper keys extracted out from the human readable work info item.
const getProperKey = (keyAlias) => {
  const key = keyAliasMap[keyAlias];

  if (key) {
    return key;
  }

  // If this happens we should probably add the key to the keyAliases...
  console.warn(`Unable to find the proper key for keyAlias: ${keyAlias}. Some data may be missing from the form`);
  return keyAlias;
};


const deconstructStandardisedSummary = (rawSummary) => {
  const summary = rawSummary.trim();
  const summaryLength = summary.length;
  const summaryObject = {};

  // we assume the summary starts with an object key
  const keyStartIndexes = [0];
  const keyEndIndexes = [];

  for (let characterIndex = 0; characterIndex < summaryLength; characterIndex++) {
    const character = summary[characterIndex];

    // This assumes the data cannot have a new line in it... Which isn't the strongest test but it's
    // all we have!
    if (character === '\n') {
      keyStartIndexes.push(characterIndex + 1);
    }
    // we look for ':' to know that it's the end of the key, but we don't want to mistake
    // a ':' in the data for the end of a key. As such we look to see if we are still looking
    // for the end of a key before looking for the end of the key
    const keyCompleted = keyStartIndexes.length > keyEndIndexes.length;
    if (keyCompleted && (character === ':')) {
      keyEndIndexes.push(characterIndex);
    }
  }

  if (keyStartIndexes.length !== keyEndIndexes.length) {
    console.error(`Unable to create accurate summary object out of summary: ${summary}.

    This error occured because beacause deconstructStandardisedSummary was unable to correctly identify the keys in the
    summary. It is likely that there is a value with ':' in it or this function is being used on a none standardisedSummary`);
    return summaryObject;
  }


  keyEndIndexes.forEach((keyEndIndex, i) => {
    const keyStartSlice = keyStartIndexes[i];
    const keyEndSlice = keyEndIndex;

    // We figure out where the value ends by looking up the index of the next key start. However,
    // if this is the last key we set the value to null;
    const nextKeyStartIndex = keyStartIndexes[i + 1] ? keyStartIndexes[i + 1] : null;
    const valueStartSlice = keyEndIndex + 1;
    // the end of this value is the start of next key -1. Unless this is the last value, in which case
    // it's the end of the summary.
    const valueEndSlice = nextKeyStartIndex ? nextKeyStartIndex - 1 : summaryLength;

    const foundKey = summary.slice(keyStartSlice, keyEndSlice);
    const key = getProperKey(foundKey);
    const foundValue = summary.slice(valueStartSlice, valueEndSlice);
    const value = foundValue.trim();
    summaryObject[key] = value;
  });

  return summaryObject;
};
*/

const getNotesJSONData = (notes) => {
  if (notes) {
    try {
      return JSON.parse(notes);
    }
    catch (error) {
      console.warn('error parsing notes JSON', error);
    }
  }
  return null;
};

const getCancelationData = (formData, network) => {
  const wiis = getWiiMetadata(formData);
  const cancelationType = CANCELLATION_INFORMATION[network];
  const cancelationWii = wiis.find((wii) => wii.type === cancelationType);
  const notes = cancelationWii ? cancelationWii.notes : null;
  return getNotesJSONData(notes);
};

const wiiContainsAutoGeneratedText = (wiiData) => {
  const {
    notes,
    summary,
  } = wiiData;
  // test both the summary and the notes field
  const conatinsAutoGenText = [notes, summary].some((field) => (
    // test against each autogenerated text
    generalInfoAutoGeneratedTexts.some((autoGenText) => (
      field && field.startsWith(autoGenText)
    ))
  ));
  return conatinsAutoGenText;
};

// work info items of type "General information" which contain auto generated text
// should be ignored when loading
const shouldWiiFromServerBeIgnored = (wiiData) => {
  const generalInfoTypes = [GENERAL_INFORMATION_FIXED, GENERAL_INFORMATION_MOBILE];
  if (generalInfoTypes.includes(wiiData.info_type)) {
    return wiiContainsAutoGeneratedText(wiiData);
  }
  return false;
};

// SIA validation for circuit check
// hence SIA validation has lots of tricky logic this fun get little complex
// devide in 4 phases get latest wii data and return sucess and failed based on 3-4 conditions
const hasServiceImpAssRemedyAtCR = (getWIIData) => {
  const {
    formSectionsByDisplayOrder,
    // dataByFormSectionId,
  } = getWIIData;
  // ** 1 This logic to get rejetion reason WII
  const infoType0 = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].dataByFieldId.work_items.value === '23340') {
      return (getWIIData.dataByFormSectionId[sectionId].submitted);
    }
    return null;
  });
  const filteredCheckSIA0 = infoType0 ? infoType0.filter((eachNotes) => ((eachNotes !== null))) : [];
  const filteredCheckSIA00 = filteredCheckSIA0 ? filteredCheckSIA0.filter((eachNotes) => ((eachNotes !== undefined))) : [];
  // const wiiInNumbers0 = filteredCheckSIA00.map((eachWIIID) => parseInt(eachWIIID.replace(/\D/g, '')));  Math.max(...wiiInNumbers0)
  const wiiInNumbers0 = filteredCheckSIA00.map((uniqueSubmmited) => moment.utc(uniqueSubmmited));

  const latestSIA0 = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].workInfoItemId !== undefined) {
      let latestDate;
      if (filteredCheckSIA00.length > 1) {
        latestDate = moment.max(...wiiInNumbers0).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0];
      }
      else {
        latestDate = filteredCheckSIA00[0] ? filteredCheckSIA00[0].split('.')[0] : null;
      }
      // const latestDate = filteredCheckSIA00.length > 1 ? moment.max(...wiiInNumbers0).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0]: (filteredCheckSIA00[0] && filteredCheckSIA00[0].split ? filteredCheckSIA00[0].split('.')[0] : null);;
      if (getWIIData.dataByFormSectionId[sectionId].submitted.includes(latestDate)) {
        return getWIIData.dataByFormSectionId[sectionId];
      }
    }
    return null;
  });
  // get latest rejection reason data after filter from null and undefiend
  const rejectionReason = latestSIA0.filter((each) => each !== null);
  // to get latest rejection/postpone wii submitted date
  const hasLatestReasonWII = rejectionReason.length !== 0 ? rejectionReason[0].submitted : '';

  // ** 2 This logic for get latest WII data for SIA
  const infoType1 = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].dataByFieldId.work_items.value === '9000') {
      return (getWIIData.dataByFormSectionId[sectionId].submitted);
    }
    return null;
  });
  const filteredCheckSIA1 = infoType1 ? infoType1.filter((eachNotes) => ((eachNotes !== null))) : [];
  const filteredCheckSIA11 = filteredCheckSIA1 ? filteredCheckSIA1.filter((eachNotes) => ((eachNotes !== undefined))) : [];
  // const wiiInNumbers = filteredCheckSIA11.map((eachWIIID) => parseInt(eachWIIID.replace(/\D/g, ''))); Math.max(...wiiInNumbers)
  const wiiSubmmited = filteredCheckSIA11.map((uniqueSubmmited) => moment.utc(uniqueSubmmited));
  // follow step get submmitted array all then check max date and print
  const latestSIA = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].workInfoItemId !== undefined) {
      // earliear it was moment.max(...wiiSubmmited).format('YYYY-MM-DDTHH:mm:ss.SS[Z]')
      let latestDate;
      if (filteredCheckSIA11.length > 1) {
        latestDate = moment.max(...wiiSubmmited).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0];
      }
      else {
        latestDate = filteredCheckSIA11[0] ? filteredCheckSIA11[0].split('.')[0] : null;
      }
      // const latestDate = filteredCheckSIA11.length > 1 ? moment.max(...wiiSubmmited).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0] : filteredCheckSIA11[0].split('.')[0];
      if (getWIIData.dataByFormSectionId[sectionId].submitted.includes(latestDate)) {
        return getWIIData.dataByFormSectionId[sectionId];
      }
    }
    return null;
  });
  // get latest SIA data after filter from null and undefiend
  const siaNotes = latestSIA.filter((each) => each !== null);
  // check wheather latest sia consist attach keyword or not
  const isSIAHasAttachKeyword = siaNotes.length !== 0 ? siaNotes[0].dataByFieldId.summary.value.includes('#ImportSIA#') : false;

  // ** 3 This logic for get latest WII data for General Information
  const infoType2 = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].dataByFieldId.work_items.value === '29000') {
      return getWIIData.dataByFormSectionId[sectionId].submitted;
    }
    return null;
  });
  const filteredCheckSIA2 = infoType2 ? infoType2.filter((eachNotes) => ((eachNotes !== null))) : [];
  const filteredCheckSIA3 = filteredCheckSIA2 ? filteredCheckSIA2.filter((eachNotes) => ((eachNotes !== undefined))) : [];
  // const filteredCheckSIA4 = filteredCheckSIA3.map((numbers) => parseInt(numbers.replace(/\D/g, '')));
  const wiiSubmmitedGeneral = filteredCheckSIA3.map((uniqueSubmmited) => moment.utc(uniqueSubmmited));

  const infoType3 = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].workInfoItemId !== undefined) {
      // get max number from filteredCheckSIA4 and return info id
      let latestDate;
      if (filteredCheckSIA3.length > 1) {
        latestDate = moment.max(...wiiSubmmitedGeneral).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0];
      }
      else {
        latestDate = filteredCheckSIA3[0] ? filteredCheckSIA3[0].split('.')[0] : null;
      }
      // const latestDate = (filteredCheckSIA3.length > 1 ? moment.max(...wiiSubmmitedGeneral).format('YYYY-MM-DDTHH:mm:ss.SS[Z]').split('.')[0] : filteredCheckSIA3[0].split('.')[0]);
      if (getWIIData.dataByFormSectionId[sectionId].submitted.includes(latestDate)) {
        return getWIIData.dataByFormSectionId[sectionId];
      }
    }
    return null;
  });
  // get latest General Info data after filter from null and undefiend
  const messagenotes = infoType3.filter((each) => each !== null);
  const generalInfoNotes = !messagenotes.length ? '' : messagenotes[0].notes;

  // to get latest submitted date from latest general info
  const getGeneralInfoLatestSubmmitedDate = messagenotes.length !== 0 ? messagenotes[0].submitted : '';
  // will return true if latest general info date is greater than latest rejection reason wii or else false
  const isRefSecondjourneyActive = (getGeneralInfoLatestSubmmitedDate !== '' && hasLatestReasonWII !== '') ? moment(getGeneralInfoLatestSubmmitedDate).isAfter(hasLatestReasonWII) : false;

  // Make flag using latest SIA ID and its summary keyword as well as latest General info notes string has latest SIA id and failed: 0 = pass
  // below one liner to avoid 0 length error
  const getSectionIdFromSIA = siaNotes.length !== 0 ? siaNotes[0].sectionId : '';
  let latestGeneralInfoSuccess = generalInfoNotes.includes('Failed: 0') && generalInfoNotes.includes(getSectionIdFromSIA) && isSIAHasAttachKeyword;

  // will return true if latest gen info note dont have Failed: 0 string which means it has >0 means has error
  let latestGeneralInfoFailed = false;
  if (generalInfoNotes.includes(getSectionIdFromSIA) && isSIAHasAttachKeyword) {
    // for failed will make array of notes get last element should not equql to Failed 0
    const lastStringOfNotes = generalInfoNotes.split('\n').slice(-1);
    latestGeneralInfoFailed = lastStringOfNotes ? (lastStringOfNotes[0] !== 'Failed: 0') : false;
    // latestGeneralInfoFailed = generalInfoNotes.includes('Failed: 1' || 'Failed: 2' || 'Failed: 3' || 'Failed: 4');
  }

  // This if to update flag if we having latest rejection wii on 2nd journey
  if (latestGeneralInfoSuccess && hasLatestReasonWII !== '') {
    latestGeneralInfoSuccess = isRefSecondjourneyActive;
  }

  // const latestGeneralInfoFailed = ((generalInfoNotes.includes('Failed: 0') && generalInfoNotes.includes(siaNotes[0].sectionId) && isSIAHasAttachKeyword)) === true ? false : true;
  return {
    latestGeneralInfoSuccess,
    latestGeneralInfoFailed,
  };
};

const hasRemedyUploadFailedSerImAss = (getWIIData) => {
  const {
    formSectionsByDisplayOrder,
    // dataByFormSectionId,
  } = getWIIData;
  const FAILED_REMEDY = '#_ATTACH_FIALED_';
  const infoType = formSectionsByDisplayOrder.map((sectionId, i) => {
    if (getWIIData.dataByFormSectionId[sectionId].dataByFieldId.work_items.value === '9000') {
      return getWIIData.dataByFormSectionId[sectionId].notes;
    }
    return null;
  });

  const filteredCheckSIA = infoType ? infoType.filter((eachNotes) => ((eachNotes !== null))) : [];
  const filteredCheckSIA1 = filteredCheckSIA ? filteredCheckSIA.filter((eachNotes) => ((eachNotes !== undefined))) : [];
  const hasFialedAttachSIA = filteredCheckSIA1 ? filteredCheckSIA1.some((str) => str.includes(FAILED_REMEDY)) : false;
  return hasFialedAttachSIA;
};

export {
  isWitHumanCreatable,
  isAttachmentMadatory,
  getNoDuplicatesWorkInfoType,
  getWiiMetadata,
  hasPeerRejectionWii,
  // getStandardisedSummary,
  // deconstructStandardisedSummary,
  getNotesJSONData,
  getCancelationData,
  shouldWiiFromServerBeIgnored,
  hasServiceImpAssRemedyAtCR,
  hasRemedyUploadFailedSerImAss,
};
